import React from 'react';
import PropTypes from 'prop-types';

import { ExclamationTriangleIcon } from '../../Shared/SearchCombobox/Icons';

/**
 * @typedef {import('../../../utils/csv/validateRosterCSV').CSVUploadResult} CSVUploadResult
 */

/**
 * Extract address parts from provider for display
 * @param {Object} provider
 */
function getOfficeAddressParts(provider) {
  if (!provider)
    return {
      hasOfficeData: false,
      streetAddress: '',
      cityState: '',
      singleLineAddress: '',
    };

  const streetAddress = provider?.office?.addressLine?.trim() || '';
  const cityState = provider?.office?.cityState?.trim() || '';
  const hasOfficeData = Boolean(streetAddress && cityState);
  const fallback = (provider?.address || '').trim();

  return {
    hasOfficeData,
    streetAddress,
    cityState,
    singleLineAddress: hasOfficeData
      ? `${streetAddress} ${cityState}`
      : fallback,
  };
}

/**
 * Displays CSV validation results with error messages
 * Shows invalid rows with their error messages above the ProviderSearch section
 * Matches Figma design with provider cards showing error badges
 *
 * @param {Object} props
 * @param {CSVUploadResult | null} props.validationResults - Validation results from CSV upload
 */
const CSVValidationResults = ({ validationResults }) => {
  if (!validationResults || validationResults.errorRows === 0) {
    return null;
  }

  const invalidResults = validationResults.results.filter((r) => !r.isValid);

  const BANNER_ERROR_TYPES = new Set([
    'max_limit_exceeded',
    'duplicate_providers',
  ]);

  const bannerMessages = invalidResults
    .flatMap((r) => r.errors || [])
    .filter((e) => BANNER_ERROR_TYPES.has(e.type))
    .map((e) => e.message)
    .filter(Boolean);

  // Banner-only errors should show as a simple message (no provider cards)
  if (bannerMessages.length > 0 && invalidResults.length === 1) {
    const errorMessage = bannerMessages[0];

    return (
      <div className="flex flex-col mb-6" data-testid="csv-validation-results">
        <div className="border border-coral-96 rounded-lg p-3 shadow-[0px_0px_2px_0px_rgba(74,74,77,0.5)]">
          <div className="flex gap-2 items-start">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm text-coral-96 font-semibold leading-5">
              {errorMessage}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Exclude banner-only results from the grouped provider error cards
  const resultsForGrouping = invalidResults.filter(
    (r) => !(r.errors || []).some((e) => BANNER_ERROR_TYPES.has(e.type)),
  );

  // Group results by their error messages (same errors = same group)
  // Create a key from sorted error messages for grouping
  const createErrorKey = (errors) => {
    return errors
      .map((e) => `${e.type}:${e.message}`)
      .sort()
      .join('||');
  };

  // Group results by error key
  const groupedResults = resultsForGrouping.reduce((groups, result) => {
    const errorKey = createErrorKey(result.errors);
    if (!groups[errorKey]) {
      groups[errorKey] = {
        errors: result.errors,
        providers: [],
      };
    }
    groups[errorKey].providers.push(result);
    return groups;
  }, {});

  return (
    <div className="flex flex-col mb-6" data-testid="csv-validation-results">
      {bannerMessages.length > 0 && (
        <div className="mb-3 border border-coral-96 rounded-lg p-3 shadow-[0px_0px_2px_0px_rgba(74,74,77,0.5)]">
          <div className="flex gap-2 items-start">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm text-coral-96 font-semibold leading-5">
              {bannerMessages[0]}
            </span>
          </div>
        </div>
      )}
      {/* Errors container with red border and scroll */}
      <div className="border border-coral-96 rounded-lg p-3 max-h-[736px] overflow-y-auto shadow-[0px_0px_2px_0px_rgba(74,74,77,0.5)]">
        {/* Header: # Upload Errors */}
        <h2 className="text-lg font-bold text-gray-90 mb-4"># Upload Errors</h2>

        <div className="flex flex-col gap-3">
          {Object.entries(groupedResults).map(([errorKey, group]) => {
            const errors = group.errors;
            const hasMultipleProviders = group.providers.length > 1;

            return (
              <div
                key={`error-group-${errorKey}`}
                className="w-full flex flex-col lg:flex-row lg:items-start p-3 gap-6 rounded-lg border border-gray-20 bg-white shadow-[0px_0px_2px_0px_rgba(74,74,77,0.5)]"
                data-testid={`csv-validation-error-group-${errorKey}`}
              >
                {/* Provider info section (left column) - show all providers in group */}
                <div className="flex flex-col gap-3 flex-shrink-0 lg:flex-1 lg:min-w-0">
                  {group.providers.map((result, providerIndex) => {
                    const provider = result.provider;
                    const {
                      hasOfficeData,
                      streetAddress,
                      cityState,
                      singleLineAddress,
                    } = getOfficeAddressParts(provider);

                    return (
                      <div
                        key={`provider-${result.rowNumber}-${providerIndex}`}
                        className={`flex gap-3 items-start ${
                          hasMultipleProviders
                            ? 'p-3 rounded-lg border border-gray-20 bg-white shadow-[0px_0px_2px_0px_rgba(74,74,77,0.5)]'
                            : ''
                        }`}
                        data-testid="provider-details"
                      >
                        {provider?.providerImageUrl && (
                          <img
                            src={provider.providerImageUrl}
                            alt={provider.displayName || 'Provider'}
                            className="w-9 h-12 flex-shrink-0 object-cover rounded"
                            loading="lazy"
                            width={36}
                            height={48}
                          />
                        )}

                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                          {/* Name and specialty on same line with bullet */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-base font-semibold text-gray-90">
                              {provider?.displayName ||
                                result.provider?.displayName ||
                                `CSV Row #${result.rowNumber}`}
                            </span>
                            {provider?.primarySpecialty?.specialtyName && (
                              <>
                                <span
                                  className="text-sm font-light text-gray-70"
                                  aria-hidden="true"
                                >
                                  •
                                </span>
                                <span className="text-sm font-light text-gray-70">
                                  {provider.primarySpecialty.specialtyName}
                                </span>
                              </>
                            )}
                          </div>
                          {singleLineAddress && (
                            <address className="not-italic text-sm font-light text-gray-70">
                              {hasOfficeData ? (
                                <>
                                  <div className="lg:hidden">
                                    {streetAddress}
                                  </div>
                                  <div className="lg:hidden">{cityState}</div>
                                  <div className="hidden lg:block">
                                    {singleLineAddress}
                                  </div>
                                </>
                              ) : (
                                <div>{singleLineAddress}</div>
                              )}
                            </address>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Error messages box (right column) - show once for the group */}
                <div
                  className="flex flex-col gap-3 p-3 rounded bg-coral-05 border border-coral-10 flex-1 lg:min-w-0"
                  role="alert"
                  aria-live="polite"
                  aria-atomic="true"
                  data-testid="error-message"
                >
                  {errors.map((error, errorIndex) => (
                    <div
                      key={`error-${errorKey}-${error.type}-${errorIndex}`}
                      className="flex gap-2 items-start"
                    >
                      <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                      <span className="text-sm text-coral-96 font-semibold leading-5">
                        {error.message}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

CSVValidationResults.propTypes = {
  validationResults: PropTypes.shape({
    results: PropTypes.arrayOf(
      PropTypes.shape({
        isValid: PropTypes.bool.isRequired,
        errors: PropTypes.arrayOf(
          PropTypes.shape({
            rowNumber: PropTypes.number.isRequired,
            type: PropTypes.string.isRequired,
            message: PropTypes.string.isRequired,
            providerName: PropTypes.string,
          }),
        ).isRequired,
        provider: PropTypes.object,
        rowNumber: PropTypes.number.isRequired,
      }),
    ).isRequired,
    totalRows: PropTypes.number.isRequired,
    validRows: PropTypes.number.isRequired,
    errorRows: PropTypes.number.isRequired,
  }),
};

export default CSVValidationResults;
